import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";

export default function NewsletterForm({ variant = "hero" }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", email: "", consent: false });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = t("newsletter.form.required_error");
    if (!values.email.trim()) next.email = t("newsletter.form.required_error");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      next.email = t("newsletter.form.email_error");
    if (!values.consent) next.consent = t("newsletter.form.consent_error");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    navigate("/thank_you");
  };

  const testIdPrefix = variant === "hero" ? "hero" : "newsletter";

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid={variant === "hero" ? "lead-generation-form" : "newsletter-form"}
      className="w-full"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label
            htmlFor={`${testIdPrefix}-name`}
            className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-700"
          >
            {t("newsletter.form.name_label")}
          </label>
          <input
            id={`${testIdPrefix}-name`}
            type="text"
            value={values.name}
            onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
            data-testid={`${testIdPrefix}-form-name-input`}
            placeholder={t("newsletter.form.name_placeholder")}
            className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
          />
          {errors.name && (
            <p
              className="mt-1 text-xs text-red-600"
              data-testid={`${testIdPrefix}-form-name-error`}
            >
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor={`${testIdPrefix}-email`}
            className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-700"
          >
            {t("newsletter.form.email_label")}
          </label>
          <input
            id={`${testIdPrefix}-email`}
            type="email"
            value={values.email}
            onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
            data-testid={`${testIdPrefix}-form-email-input`}
            placeholder={t("newsletter.form.email_placeholder")}
            className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
          />
          {errors.email && (
            <p
              className="mt-1 text-xs text-red-600"
              data-testid={`${testIdPrefix}-form-email-error`}
            >
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <label className="mt-4 flex items-start gap-3 text-sm text-slate-600">
        <input
          type="checkbox"
          checked={values.consent}
          onChange={(e) =>
            setValues((v) => ({ ...v, consent: e.target.checked }))
          }
          data-testid={`${testIdPrefix}-form-consent-checkbox`}
          className="mt-0.5 h-4 w-4 rounded border-slate-400 text-emerald-600 focus:ring-emerald-500"
        />
        <span>{t("newsletter.form.consent")}</span>
      </label>
      {errors.consent && (
        <p
          className="mt-1 text-xs text-red-600"
          data-testid={`${testIdPrefix}-form-consent-error`}
        >
          {errors.consent}
        </p>
      )}

      <button
        type="submit"
        data-testid={`${testIdPrefix}-form-submit-button`}
        className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-emerald-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-emerald-700 sm:w-auto"
      >
        {t("newsletter.form.submit")}
      </button>
    </form>
  );
}
